import { Input, type InputProps } from '@library/components/inputs';
import { useCallback, useMemo } from 'react';
import { cn } from '@library/component-utils/cn';

export type RadioGroupOptions =
  | Array<string>
  | Array<{ option: string; description: string; value: string }>;

export interface RadioGroupProps extends InputProps {
  className?: string;
  containerClassName?: string;
  dataTestId?: string;
  disabled?: boolean;
  id: string;
  inputClassName?: string;
  labelClassName?: string;
  onValueChange: (value: string) => void;
  radioOptions: RadioGroupOptions;
  selectedOption?: string;
}

export const RadioGroup = ({
  className = '',
  containerClassName = '',
  dataTestId = '',
  disabled = false,
  id,
  inputClassName = '',
  labelClassName = '',
  onValueChange,
  radioOptions,
  selectedOption = ''
}: RadioGroupProps) => {
  const formattedRadioOptions = useMemo(
    () =>
      radioOptions.map((option) => {
        if (typeof option === 'string') {
          return { option, description: null, value: option };
        }
        return option;
      }),
    [radioOptions]
  );

  const handleChange = useCallback(
    (option: string) => () => onValueChange(option),
    [onValueChange]
  );

  return (
    <div
      id={id}
      data-testid={`radio-group-${dataTestId || id}`}
      className={cn('flex w-full items-center gap-10', className)}
    >
      {formattedRadioOptions.map(({ option, description, value }) => (
        <div
          key={option}
          className={cn('flex items-center', containerClassName)}
        >
          <Input
            className={cn(
              'border-soClean-180 checked:border-soClean-100 checked:before:bg-soClean-100 checked:focus:bg-soFresh-120 bg-soFresh-120 checked:focus:outline-hidden focus-visible:outline-hidden relative size-4 appearance-none rounded-full border-2 before:absolute before:inset-1 before:rounded-full before:bg-transparent checked:bg-transparent',
              inputClassName
            )}
            data-testid={`radio-option-${option}`}
            defaultChecked={value === selectedOption}
            disabled={disabled}
            id={option}
            name="radio-option"
            onValueChange={handleChange(value)}
            type="radio"
          />
          <label
            htmlFor={option}
            className={cn('ml-2 block text-sm/6', labelClassName)}
          >
            <span className="text-soClean-100 font-medium">{option}</span>
            {description && (
              <span className="text-soClean-180">{description}</span>
            )}
          </label>
        </div>
      ))}
    </div>
  );
};
